import {
  Bot,
  Calculator,
  CalendarDays,
  ChartColumn,
  Clock,
  FileText,
  Heart,
  Inbox,
  Leaf,
  LifeBuoy,
  MessageSquare,
  Package,
  Plus,
  Search,
  Send,
  ShoppingCart,
  Truck,
} from "@/components/icons";
import type { NavItem, NavSection } from "@/components/app-sidebar/types";
import {
  TOOL_CATALOG,
  getTool,
  toolDashboardHref,
  toolDashboardTitle,
  type ToolId,
} from "@/lib/tools/catalog";
import { TOOL_ICONS } from "../../tools/_components/tool-icons";

export const ORDERS_ITEM: NavItem = {
  label: "Orders",
  href: "/dashboard/orders",
  icon: ShoppingCart,
  prefetch: true,
};

export const QUOTES_ITEM: NavItem = {
  label: "Quotes",
  href: "/dashboard/quotes",
  icon: FileText,
  prefetch: true,
};

export const BROWSE_ITEM: NavItem = {
  label: "Browse",
  href: "/listing",
  icon: Search,
};

export const MY_LISTINGS_ITEM: NavItem = {
  label: "Listings",
  href: "/dashboard/inventory",
  icon: Package,
};

export const BUYER_REQUESTS_ITEM: NavItem = {
  label: "Buyer requests",
  shortLabel: "Requests",
  href: "/dashboard/rfqs",
  icon: Inbox,
  requiredAccountTypes: ["vendor"],
};

export const LOADS_ITEM: NavItem = {
  label: "Loads",
  href: "/dashboard/carrier/loads",
  icon: Truck,
};

export const CARRIER_BIDS_ITEM: NavItem = {
  label: "Carrier bids",
  href: "/dashboard/carrier/bids",
  icon: Send,
};

export const HELP_ITEM: NavItem = {
  label: "Help",
  href: "/dashboard/support",
  icon: LifeBuoy,
  exact: true,
};

const ORDERS_SECTION: NavSection = {
  requiredAccountTypes: ["buyer", "vendor"],
  items: [
    ORDERS_ITEM,
    QUOTES_ITEM,
    {
      label: "Schedule",
      href: "/dashboard/calendar",
      icon: CalendarDays,
      prefetch: true,
      activePaths: ["/dashboard/recurring-orders", "/dashboard/schedule"],
    },
    {
      label: "Impact",
      href: "/dashboard/impact",
      icon: Leaf,
      requiredAccountTypes: ["buyer"],
    },
  ],
};

const BUY_SECTION: NavSection = {
  title: "Buy",
  requiredAccountTypes: ["buyer", "vendor"],
  items: [
    BROWSE_ITEM,
    {
      label: "Request a quote",
      href: "/dashboard/rfqs/create",
      icon: MessageSquare,
    },
    {
      label: "Saved",
      href: "/dashboard/saved",
      icon: Heart,
      prefetch: true,
    },
  ],
};

const SELL_SECTION: NavSection = {
  title: "Sell",
  requiredAccountTypes: ["vendor"],
  items: [
    MY_LISTINGS_ITEM,
    BUYER_REQUESTS_ITEM,
    { label: "New listing", href: "/dashboard/inventory/new", icon: Plus },
    {
      label: "Finish listings",
      href: "/dashboard/inventory/complete",
      icon: Clock,
      prefetch: true,
    },
  ],
};

const SIDEBAR_TOOL_IDS = [
  "truck-loading",
  "packaging-designer",
  "freight-calculator",
  "price-index",
  "grade",
] as const satisfies ReadonlyArray<ToolId>;

function toolNavItem(toolId: ToolId): NavItem {
  const tool = getTool(toolId);
  return {
    label: toolDashboardTitle(tool),
    href: toolDashboardHref(tool),
    icon: TOOL_ICONS[toolId],
    activePaths: [tool.dashboardPath],
  };
}

const CALCULATORS_ENTRY: NavItem = {
  label: "Calculators",
  href: "/dashboard/tools/calculators",
  icon: Calculator,
  activePaths: TOOL_CATALOG.filter(
    (tool) => tool.group === "calculator" && !SIDEBAR_TOOL_IDS.some((id) => id === tool.id),
  ).map((tool) => tool.dashboardPath),
};

const TOOLS_SECTION: NavSection = {
  title: "Tools",
  hideForAccountTypes: ["freight_carrier"],
  items: [
    ...SIDEBAR_TOOL_IDS.map(toolNavItem),
    CALCULATORS_ENTRY,
    {
      label: "Analytics",
      href: "/dashboard/analytics",
      icon: ChartColumn,
      requiredAccountTypes: ["vendor"],
    },
  ],
};

const FREIGHT_SECTION: NavSection = {
  title: "Freight",
  requiredAccountTypes: ["freight_carrier"],
  items: [
    LOADS_ITEM,
    CARRIER_BIDS_ITEM,
  ],
};

export function buildDashboardNavigation(): NavSection[] {
  return [
    ORDERS_SECTION,
    BUY_SECTION,
    SELL_SECTION,
    FREIGHT_SECTION,
    TOOLS_SECTION,
    {
      items: [
        {
          label: "Ask Repackify",
          href: "/dashboard/ai",
          icon: Bot,
          opensAssistant: true,
          hideForAccountTypes: ["freight_carrier"],
        },
        HELP_ITEM,
      ],
    },
  ];
}
